import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Zap, Loader2, GraduationCap, Briefcase, ShieldCheck } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [role, setRole] = useState("alternant");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [nom, setNom] = useState("");
    const [prenom, setPrenom] = useState("");
    const [phone, setPhone] = useState("");
    const [etablissement, setEtablissement] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [activationCode, setActivationCode] = useState("");
    const [niveau, setNiveau] = useState(""); // alternants only
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [acceptCgu, setAcceptCgu] = useState(false);
    // Profawz now operates on an invitation-only model — no Stripe pricing surfaces.
    const freeMode = true;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (loading) return; // hard guard against double-submit
        if (!acceptCgu) {
            setError("Vous devez accepter les CGU et la politique de confidentialité pour continuer.");
            return;
        }
        if ((role === "alternant" || role === "formateur") && !inviteCode.trim()) {
            setError("Le code d'établissement fourni par votre responsable est requis.");
            return;
        }
        if (role === "responsable" && !activationCode.trim()) {
            setError("Le code d'activation Profawz est requis. Contactez contact@profawz.fr pour activer votre établissement.");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const user = await register({
                email,
                password,
                role,
                nom_etablissement: etablissement,
                nom,
                prenom,
                phone,
                invite_code: (role === "alternant" || role === "formateur") ? inviteCode.trim().toUpperCase() : undefined,
                activation_code: role === "responsable" ? activationCode.trim().toUpperCase() : undefined,
                niveau: role === "alternant" ? (niveau || undefined) : undefined,
            });
            if ((user.role === "formateur" || user.role === "responsable")) navigate("/dashboard");
            else navigate("/dashboard");
        } catch (err) {
            // Race condition guard: maybe registration succeeded server-side
            // and the cookie is set, but the response was interrupted.
            try {
                const { data } = await api.get("/auth/me");
                if (data && data.email) {
                    navigate("/dashboard");
                    return;
                }
            } catch (meErr) {
                console.debug("[Register] /auth/me fallback failed", meErr);
            }
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-ink text-white p-12 grid-bg">
                <Link to="/" className="flex items-center gap-2.5" data-testid="register-logo-link">
                    <span className="w-7 h-7 bg-orange text-ink flex items-center justify-center">
                        <Zap size={16} strokeWidth={2.5} />
                    </span>
                    <span className="h-display text-lg font-semibold">Profawz</span>
                </Link>
                <div className="max-w-md">
                    <div className="label-eyebrow text-orange/80 mb-3">Deux espaces, une plateforme</div>
                    <h2 className="h-display text-4xl font-semibold leading-tight">
                        Choisissez votre profil :<br />
                        <span className="text-orange">alternant ou formateur.</span>
                    </h2>
                    <ul className="mt-6 space-y-2 text-sm text-white/70">
                        <li>· Alternants : compte gratuit, soumission illimitée</li>
                        <li>· Formateurs : compte gratuit, code d'établissement requis</li>
                        <li>· Responsables : <span className="text-emerald-300 font-semibold">accès activé par l'équipe Profawz</span></li>
                        <li>· Aucune limite d'étudiants par établissement</li>
                    </ul>
                </div>
                <div className="text-xs font-mono text-white/40">© {new Date().getFullYear()} Profawz</div>
            </div>

            <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-12">
                <div className="max-w-md w-full mx-auto">
                    <div className="label-eyebrow mb-2">Inscription</div>
                    <h1 className="h-display text-3xl sm:text-4xl font-semibold mb-2">Créer un compte</h1>
                    <p className="text-ink/60 text-sm mb-8">
                        Déjà inscrit ?{" "}
                        <Link to="/login" className="font-semibold text-orange hover:underline" data-testid="register-login-link">
                            Se connecter
                        </Link>
                    </p>

                    {/* Role selector */}
                    <div className="grid grid-cols-3 gap-2 mb-6" role="radiogroup">
                        <button
                            type="button"
                            onClick={() => setRole("alternant")}
                            className={`p-3 border rounded-sm text-left transition-all ${
                                role === "alternant"
                                    ? "border-orange bg-orange/5"
                                    : "border-rule hover:border-ink/40"
                            }`}
                            data-testid="register-role-alternant"
                        >
                            <GraduationCap size={18} className="text-orange mb-1.5" strokeWidth={1.7} />
                            <div className="font-semibold text-sm">Alternant</div>
                            <div className="text-[11px] text-muted2 mt-0.5">Étudiant BTS</div>
                            <div className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 mt-2 text-[10px]">Gratuit</div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setRole("formateur")}
                            className={`p-3 border rounded-sm text-left transition-all ${
                                role === "formateur"
                                    ? "border-orange bg-orange/5"
                                    : "border-rule hover:border-ink/40"
                            }`}
                            data-testid="register-role-formateur"
                        >
                            <Briefcase size={18} className="text-orange mb-1.5" strokeWidth={1.7} />
                            <div className="font-semibold text-sm">Formateur</div>
                            <div className="text-[11px] text-muted2 mt-0.5">Cours & TPs</div>
                            <div className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 mt-2 text-[10px]">Gratuit</div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setRole("responsable")}
                            className={`p-3 border rounded-sm text-left transition-all ${
                                role === "responsable"
                                    ? "border-orange bg-orange/5"
                                    : "border-rule hover:border-ink/40"
                            }`}
                            data-testid="register-role-responsable"
                        >
                            <ShieldCheck size={18} className="text-orange mb-1.5" strokeWidth={1.7} />
                            <div className="font-semibold text-sm">Responsable</div>
                            <div className="text-[11px] text-muted2 mt-0.5">de formation</div>
                            <div className="pill mt-2 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Sur invitation
                            </div>
                        </button>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                        {(role === "alternant" || role === "formateur") && (
                            <>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="label-eyebrow mb-2 block">Prénom</label>
                                        <input
                                            type="text"
                                            value={prenom}
                                            onChange={(e) => setPrenom(e.target.value)}
                                            className="field-input"
                                            placeholder="Lucas"
                                            data-testid="register-prenom-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="label-eyebrow mb-2 block">Nom</label>
                                        <input
                                            type="text"
                                            value={nom}
                                            onChange={(e) => setNom(e.target.value)}
                                            className="field-input"
                                            placeholder="Martin"
                                            data-testid="register-nom-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Code d'établissement *</label>
                                    <input
                                        type="text"
                                        value={inviteCode}
                                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                        className="field-input font-mono uppercase"
                                        placeholder="BTS-XXXXXX"
                                        required
                                        data-testid="register-invite-code"
                                    />
                                    <p className="text-[11px] text-muted2 mt-1.5">
                                        Demandez ce code à votre <strong>responsable de formation</strong>. Sans ce code, votre compte ne pourra pas être rattaché à votre établissement.
                                    </p>
                                </div>
                                {role === "alternant" && (
                                    <div>
                                        <label className="label-eyebrow mb-2 block">Année BTS (optionnel)</label>
                                        <div className="grid grid-cols-2 gap-2" data-testid="register-niveau-selector">
                                            <button
                                                type="button"
                                                onClick={() => setNiveau(niveau === "1ere" ? "" : "1ere")}
                                                className={`p-3 text-sm border transition-colors ${niveau === "1ere" ? "bg-ink text-white border-ink" : "border-rule hover:border-ink/40"}`}
                                                data-testid="register-niveau-1ere"
                                            >
                                                1ère année BTS
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setNiveau(niveau === "2eme" ? "" : "2eme")}
                                                className={`p-3 text-sm border transition-colors ${niveau === "2eme" ? "bg-ink text-white border-ink" : "border-rule hover:border-ink/40"}`}
                                                data-testid="register-niveau-2eme"
                                            >
                                                2ème année BTS
                                            </button>
                                        </div>
                                        <p className="text-[11px] text-muted2 mt-1.5">Indique ton année actuelle — ton responsable pourra t'envoyer des messages ciblés par promo.</p>
                                    </div>
                                )}
                            </>
                        )}
                        {role === "responsable" && (
                            <div className="panel p-4 bg-orange/5 border-orange/30">
                                <label className="label-eyebrow mb-2 block flex items-center gap-1.5">
                                    <ShieldCheck size={12} className="text-orange" /> Code d'activation Profawz *
                                </label>
                                <input
                                    type="text"
                                    value={activationCode}
                                    onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                                    className="field-input font-mono uppercase"
                                    placeholder="PROF-XXXXXX"
                                    required
                                    data-testid="register-activation-code"
                                />
                                <p className="text-[11px] text-muted2 mt-1.5 leading-relaxed">
                                    Profawz fonctionne <strong>sur invitation</strong>. Ce code est délivré par l'équipe Profawz après validation de votre demande d'activation. Si vous n'avez pas encore de code, écrivez à <a href="mailto:contact@profawz.fr" className="text-orange underline">contact@profawz.fr</a>.
                                </p>
                            </div>
                        )}
                        <div>
                            <label className="label-eyebrow mb-2 block">
                                {role === "formateur" ? "Établissement" : "Établissement (optionnel)"}
                            </label>
                            <input
                                type="text"
                                value={etablissement}
                                onChange={(e) => setEtablissement(e.target.value)}
                                className="field-input"
                                placeholder="Lycée Sainte Famille"
                                data-testid="register-etablissement-input"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                                className="field-input"
                                placeholder={role === "formateur" ? "responsable@etablissement.fr" : "vous@email.fr"}
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                autoComplete="email"
                                inputMode="email"
                                data-testid="register-email-input"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">Mot de passe (6 caractères min.)</label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="field-input"
                                placeholder="••••••••"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                autoComplete="new-password"
                                data-testid="register-password-input"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">Téléphone</label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="field-input"
                                placeholder="06 12 34 56 78"
                                data-testid="register-phone-input"
                            />
                        </div>
                        {error && (
                            <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="register-error">
                                {error}
                            </div>
                        )}

                        <label className="flex items-start gap-2.5 text-xs text-ink/80 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={acceptCgu}
                                onChange={(e) => setAcceptCgu(e.target.checked)}
                                className="mt-0.5 w-4 h-4 accent-orange shrink-0"
                                data-testid="register-accept-cgu"
                            />
                            <span>
                                J'accepte les <Link to="/cgu" target="_blank" className="text-orange underline">CGU</Link>, les <Link to="/cgv" target="_blank" className="text-orange underline">CGV</Link> et la <Link to="/confidentialite" target="_blank" className="text-orange underline">politique de confidentialité</Link> de Profawz.
                            </span>
                        </label>

                        <button type="submit" disabled={loading} className="btn-primary w-full !py-3" data-testid="register-submit-button">
                            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                            {loading ? "Création…" : "Créer mon compte"}
                        </button>
                        <p className="text-[11px] text-muted2 text-center">
                            {role === "responsable"
                                ? "Compte gratuit. L'équipe Profawz active votre établissement après contact."
                                : role === "formateur"
                                ? "Compte 100% gratuit. L'accès à votre établissement est activé par votre responsable de formation."
                                : "Compte 100% gratuit. Demandez à votre responsable d'activer la plateforme."}
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
